import React, { useState, useEffect, useRef, useCallback } from 'react';

interface SearchableDropdownProps<T> {
  id: string;
  label: string;
  showLabel?: boolean;
  placeholder?: string;
  items: T[];
  value: number | string | undefined;
  onChange: (value: number | string) => void;
  getItemValue: (item: T) => number | string;
  getItemLabel: (item: T) => string;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  className?: string;
  searchPlaceholder?: string;
  noItemsMessage?: string;
  filterItems?: (items: T[], searchTerm: string) => T[];
  size?: 'sm' | 'md' | 'lg';
}

function SearchableDropdown<T>({
  id,
  label,
  showLabel = true,
  placeholder = 'Select...',
  items,
  value,
  onChange,
  getItemValue,
  getItemLabel,
  disabled = false,
  error,
  required = false,
  className = '',
  searchPlaceholder = 'Type to search...',
  noItemsMessage = 'No items found',
  filterItems,
  size = 'md',
}: SearchableDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState<T[]>(items);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Default filter function if none provided
  const defaultFilter = useCallback((items: T[], term: string) => {
    if (!term) return items;
    const lowercaseTerm = term.toLowerCase();
    return items.filter(item => 
      getItemLabel(item).toLowerCase().includes(lowercaseTerm)
    );
  }, [getItemLabel]);

  // Update filtered items when search term or items change
  useEffect(() => {
    const filterFn = filterItems || defaultFilter;
    setFilteredItems(filterFn(items, searchTerm));
  }, [searchTerm, items, filterItems, defaultFilter]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedItem = items.find(item => getItemValue(item) === value);
  const displayValue = selectedItem ? getItemLabel(selectedItem) : '';

  const handleSelect = (item: T) => {
    onChange(getItemValue(item));
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className={`searchable-dropdown ${className}`} ref={dropdownRef}>
      {showLabel && (
        <label htmlFor={id} className="form-label">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}
      
      <div className="position-relative">
        <button
          type="button"
          id={id}
          className={`form-select ${size === 'sm' ? 'form-select-sm' : ''} text-start ${error ? 'is-invalid' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          style={{
            background: disabled ? '#e9ecef' : 'white',
            cursor: disabled ? 'not-allowed' : 'pointer'
          }}
        >
          <span className={displayValue ? '' : 'text-muted'}>
            {displayValue || placeholder}
          </span>
        </button>

        {isOpen && (
          <div className="dropdown-menu show w-100">
            <div
              className="sticky-top bg-white border-bottom"
              style={{ padding: '0.5rem 0' }}
            >
              <input
                ref={searchInputRef}
                type="text"
                className="form-control form-control-sm w-100"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
                style={{ boxSizing: 'border-box' }}
              />
            </div>

            <div className="dropdown-items">
              {filteredItems.length === 0 ? (
                <div className="dropdown-item text-muted text-center">
                  {noItemsMessage}
                </div>
              ) : (
                filteredItems.map((item, index) => {
                  const itemValue = getItemValue(item);
                  const isSelected = itemValue === value;
                  
                  return (
                    <button
                      key={itemValue}
                      type="button"
                      className={`dropdown-item ${isSelected ? 'active' : ''}`}
                      onClick={() => handleSelect(item)}
                      style={{ 
                        cursor: 'pointer',
                        background: isSelected ? 'var(--primary)' : undefined,
                        color: isSelected ? 'white' : undefined,
                        display: 'block',
                        width: '100%',
                        textAlign: 'left'
                      }}
                    >
                      {getItemLabel(item)}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
}

export default SearchableDropdown;
